import React, { useState } from 'react';
import { Button } from './ui/button';
import { Sheet, SheetContent, SheetTrigger } from './ui/sheet';
import { Menu, Phone, MapPin, Package } from 'lucide-react';
import { companyInfo } from '../data/mock';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);

  React.useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { label: 'Solutions', href: '/#solutions' },
    { label: 'About', href: '/#about' },
    { label: 'Partners', href: '/partners' },
    { label: 'Contact', href: '/#contact' }
  ];

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      isScrolled 
        ? 'bg-white/95 backdrop-blur-md shadow-lg' 
        : 'bg-transparent'
    }`}>
      {/* Top Bar */}
      <div className={`transition-all duration-300 ${
        isScrolled ? 'h-0 overflow-hidden opacity-0' : 'h-10 opacity-100'
      } bg-slate-900`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full flex items-center justify-between text-sm">
          <div className="flex items-center gap-6 text-slate-300">
            <a href="tel:+18005559999" className="flex items-center gap-2 hover:text-amber-400 transition-colors">
              <Phone size={14} />
              <span>{companyInfo.phone}</span>
            </a>
            <span className="hidden sm:flex items-center gap-2">
              <MapPin size={14} />
              <span>New York, NY</span>
            </span>
          </div>
        </div>
      </div>

      {/* Main Header */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <a href="/" className="flex items-center gap-3 group">
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center transition-all duration-300 ${
              isScrolled ? 'bg-slate-900' : 'bg-white/10 backdrop-blur-sm'
            }`}>
              <Package className={`w-7 h-7 transition-colors duration-300 ${
                isScrolled ? 'text-amber-500' : 'text-white'
              }`} />
            </div>
            <div>
              <span className={`font-bold text-xl tracking-tight transition-colors duration-300 ${
                isScrolled ? 'text-slate-900' : 'text-white'
              }`}>
                AXIOM
              </span>
              <span className={`block text-xs font-medium tracking-widest transition-colors duration-300 ${
                isScrolled ? 'text-slate-500' : 'text-slate-300'
              }`}>
                GLOBAL FREIGHT
              </span>
            </div>
          </a>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <a
                key={link.label}
                href={link.href}
                data-testid={`nav-link-${link.label.toLowerCase()}`}
                className={`flex items-center gap-1 font-medium transition-colors duration-200 hover:text-amber-500 ${
                  isScrolled ? 'text-slate-700' : 'text-white'
                }`}
              >
                {link.label}
              </a>
            ))}
          </nav>

          {/* CTAs */}
          <div className="hidden lg:flex items-center gap-3">
            <a href="/#contact">
              <Button
                data-testid="header-quote-button"
                className="bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold px-6 shadow-lg shadow-amber-500/25 hover:shadow-amber-500/40 transition-all duration-300"
              >
                Request a Quote
              </Button>
            </a>
          </div>

          {/* Mobile Menu */}
          <Sheet>
            <SheetTrigger asChild className="lg:hidden">
              <Button variant="ghost" size="icon" className={isScrolled ? 'text-slate-900' : 'text-white'}>
                <Menu size={24} />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-80 bg-slate-900 border-slate-800">
              <div className="flex flex-col gap-6 mt-8">
                <a href="/" className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-amber-500 rounded-lg flex items-center justify-center">
                    <Package className="w-6 h-6 text-slate-900" />
                  </div>
                  <span className="font-bold text-xl text-white">AXIOM</span>
                </a>
                {navLinks.map((link) => (
                  <a
                    key={link.label}
                    href={link.href}
                    data-testid={`mobile-nav-link-${link.label.toLowerCase()}`}
                    className="text-lg font-medium text-slate-300 hover:text-amber-400 transition-colors py-2"
                  >
                    {link.label}
                  </a>
                ))}
                <div className="border-t border-slate-800 pt-6 flex flex-col gap-3">
                  <a href="/#contact">
                    <Button data-testid="mobile-header-quote-button" className="w-full bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold">
                      Request a Quote
                    </Button>
                  </a>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};

export default Header;
